import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatNativeDateModule } from '@angular/material/core';
import { MatCalendar, MatCalendarCellClassFunction, MatDatepickerModule } from '@angular/material/datepicker';
import moment from 'moment';

/**
 * A self-contained date+time range picker: a MatCalendar for two-click day selection, paired with
 * typeable From/To date and time inputs, capped at `maxDays`. The body only — the caller supplies
 * its own trigger and popover chrome, seeds it via [start]/[end], and commits on (apply).
 *
 * Extracted from the Network Overview dashboard's inline picker so the dashboard and the dial-logs
 * chart share one implementation (localized formats, 30-day cap, in-calendar greying, validation).
 */
@Component({
    selector: 'app-date-range-calendar',
    standalone: true,
    imports: [CommonModule, MatDatepickerModule, MatNativeDateModule],
    templateUrl: './date-range-calendar.component.html',
    styleUrls: ['./date-range-calendar.component.scss'],
})
export class DateRangeCalendarComponent implements OnInit {
    /** Longest window allowed, in calendar days. */
    @Input() maxDays = 30;
    /** Initial window (epoch ms) — seeds the calendar and From/To fields. */
    @Input() start: number | null = null;
    @Input() end: number | null = null;
    /** View-only: show the window highlighted on the calendar but allow no editing - the day
     * grid, From/To fields and Apply/Cancel are suppressed. Month navigation stays available. */
    @Input() readOnly = false;
    /** Emitted when the user commits a valid range (epoch ms). */
    @Output() apply = new EventEmitter<{ start: number; end: number }>();
    /** Emitted when the user dismisses without applying. */
    @Output() cancel = new EventEmitter<void>();

    /** The range being picked (start then end, over two clicks). */
    calStart: Date | null = null;
    calEnd: Date | null = null;
    /** Time-of-day (HH:mm) paired with the calendar dates for the From/To inputs. */
    customStartTime = '00:00';
    customEndTime = '23:59';
    /** Caps the calendar at the present. */
    readonly today = new Date();
    /** Live calendar bounds: once a start is picked, the other end is limited to ±maxDays
     * (never past today), so out-of-range days grey out in the picker itself. */
    calMinDate: Date | null = null;
    calMaxDate: Date = this.today;

    @ViewChild(MatCalendar) private calendar?: MatCalendar<Date>;

    ngOnInit(): void {
        if (this.start != null) {
            this.calStart = new Date(this.start);
            this.customStartTime = moment(this.start).format('HH:mm');
        }
        if (this.end != null) {
            this.calEnd = new Date(this.end);
            this.customEndTime = moment(this.end).format('HH:mm');
        }
        this.updateCalBounds();
    }

    /** The date the calendar draws its focus ring on — the end while a range is mid-selection. */
    get calSelected(): Date | null {
        return this.calEnd || this.calStart;
    }

    /** Typeable date strings for the From/To inputs, in the locale's short format. */
    get customStartDate(): string {
        return this.calStart ? moment(this.calStart).format('L') : '';
    }
    get customEndDate(): string {
        return this.calEnd ? moment(this.calEnd).format('L') : '';
    }
    /** The locale's date pattern, shown as the input placeholder (e.g. "MM/DD/YYYY"). */
    get dateHint(): string {
        return moment.localeData().longDateFormat('L');
    }
    /** Time-of-day in the locale's short time format (e.g. "1:41 PM"). */
    get customStartTimeDisplay(): string {
        return moment(this.customStartTime, 'HH:mm').format('LT');
    }
    get customEndTimeDisplay(): string {
        return moment(this.customEndTime, 'HH:mm').format('LT');
    }
    /** The locale's time pattern, shown as the input placeholder (e.g. "h:mm A"). */
    get timeHint(): string {
        return moment.localeData().longDateFormat('LT');
    }
    /** Apply is enabled once both ends are set, start is before end, and the span fits the cap. */
    get canApply(): boolean {
        return (
            this.combineCustom(this.calStart, this.customStartTime) !== null &&
            this.combineCustom(this.calEnd, this.customEndTime) !== null &&
            !this.rangeError
        );
    }
    /** Why Apply is blocked (empty when the range is valid), shown under the inputs. */
    get rangeError(): string {
        const s = this.combineCustom(this.calStart, this.customStartTime);
        const e = this.combineCustom(this.calEnd, this.customEndTime);
        if (s === null || e === null) {
            return '';
        }
        if (s >= e) {
            return 'Start must be before end.';
        }
        // Measured by calendar days so it lines up with the greyed-out picker (and catches typed
        // dates that bypass the calendar bounds).
        const dayGap = moment(this.calEnd).startOf('day').diff(moment(this.calStart).startOf('day'), 'days');
        if (dayGap > this.maxDays) {
            return `Range can't exceed ${this.maxDays} days.`;
        }
        return '';
    }

    /** Paints the range highlight onto the calendar cells (start / end / in-between). */
    dateClass: MatCalendarCellClassFunction<Date> = (date, view) => {
        if (view !== 'month' || !date || (!this.calStart && !this.calEnd)) {
            return '';
        }
        const d = this.startOfDay(date).getTime();
        const s = this.calStart ? this.startOfDay(this.calStart).getTime() : null;
        const e = this.calEnd ? this.startOfDay(this.calEnd).getTime() : null;
        if (s !== null && d === s) {
            return 'drc-range-start';
        }
        if (e !== null && d === e) {
            return 'drc-range-end';
        }
        if (s !== null && e !== null && d > s && d < e) {
            return 'drc-in-range';
        }
        return '';
    };

    /** Two-click range selection on the calendar; fills the From/To dates. Commit is via Apply. */
    onCalSelect(date: Date | null): void {
        if (!date || this.readOnly) {
            return;
        }
        const picked = this.startOfDay(date);
        if (!this.calStart || (this.calStart && this.calEnd)) {
            this.calStart = picked;
            this.calEnd = null;
        } else if (picked.getTime() < this.calStart.getTime()) {
            this.calEnd = this.calStart;
            this.calStart = picked;
        } else {
            this.calEnd = picked;
        }
        this.updateCalBounds();
        // Re-run dateClass so the range highlight repaints after the pick.
        this.calendar?.updateTodaysDate();
    }

    /** Constrain the calendar to a valid second pick: within maxDays of the start (either direction,
     * since an earlier pick swaps to become the start) and never past today. Cleared once a range
     * is complete so the next fresh start can land anywhere. */
    private updateCalBounds(): void {
        if (this.calStart && !this.calEnd) {
            this.calMinDate = moment(this.calStart).subtract(this.maxDays, 'days').toDate();
            const cap = moment(this.calStart).add(this.maxDays, 'days').toDate();
            this.calMaxDate = cap.getTime() < this.today.getTime() ? cap : this.today;
        } else {
            this.calMinDate = null;
            this.calMaxDate = this.today;
        }
    }

    /** From/To date + time inputs, kept in sync with the calendar. The typed value is parsed
     * leniently, then the field is rewritten to the canonical form (or reverted if unparseable). */
    onStartDateInput(ev: Event): void {
        const el = ev.target as HTMLInputElement;
        const d = this.parseDateInput(el.value);
        if (d) {
            this.calStart = d;
            this.calendar?.updateTodaysDate();
        }
        el.value = this.customStartDate;
    }
    onEndDateInput(ev: Event): void {
        const el = ev.target as HTMLInputElement;
        const d = this.parseDateInput(el.value);
        if (d) {
            this.calEnd = d;
            this.calendar?.updateTodaysDate();
        }
        el.value = this.customEndDate;
    }
    onStartTimeInput(ev: Event): void {
        const el = ev.target as HTMLInputElement;
        const t = this.parseTimeInput(el.value);
        if (t) {
            this.customStartTime = t;
        }
        el.value = this.customStartTimeDisplay;
    }
    onEndTimeInput(ev: Event): void {
        const el = ev.target as HTMLInputElement;
        const t = this.parseTimeInput(el.value);
        if (t) {
            this.customEndTime = t;
        }
        el.value = this.customEndTimeDisplay;
    }

    /** Commit the custom range: each end's calendar date combined with its time-of-day. */
    onApply(): void {
        const s = this.combineCustom(this.calStart, this.customStartTime);
        const e = this.combineCustom(this.calEnd, this.customEndTime);
        if (s === null || e === null || this.rangeError) {
            return;
        }
        this.apply.emit({ start: s, end: e });
    }

    onCancel(): void {
        this.cancel.emit();
    }

    /** Accept the locale's short format (padded 'L' or loose 'l') plus ISO; reject blanks and
     * dates past today (the calendar's cap). */
    private parseDateInput(value: string): Date | null {
        const m = moment((value || '').trim(), ['L', 'l', 'YYYY-MM-DD'], false);
        if (!m.isValid()) {
            return null;
        }
        const d = this.startOfDay(m.toDate());
        return d.getTime() > this.startOfDay(this.today).getTime() ? null : d;
    }

    /** Accept 12h ("1:41 PM", "1pm") or 24h ("13:41") typed times; return canonical "HH:mm". */
    private parseTimeInput(value: string): string | null {
        const m = moment((value || '').trim(), ['LT', 'h:mm A', 'h:mmA', 'h A', 'hA', 'H:mm', 'HH:mm', 'h:mm'], false);
        return m.isValid() ? m.format('HH:mm') : null;
    }

    /** A calendar date's Y/M/D combined with an HH:mm time, as epoch ms (null if no date). */
    private combineCustom(date: Date | null, time: string): number | null {
        if (!date) {
            return null;
        }
        const [h, min] = (time || '00:00').split(':').map((n) => Number(n));
        const d = new Date(date);
        d.setHours(Number.isFinite(h) ? h : 0, Number.isFinite(min) ? min : 0, 0, 0);
        return d.getTime();
    }

    private startOfDay(date: Date): Date {
        const d = new Date(date);
        d.setHours(0, 0, 0, 0);
        return d;
    }
}
